import store from '@/store'
import { computed, readonly, ref, watchEffect } from 'vue'
import { useRouteParams } from '@vueuse/router'
import { boardNavigationService } from '@/services/board-navigation.service'

/**
 * Hook that encapsulates data related to a board
 * Enhanced with centralized board navigation service
 */
export default function useBoard() {
  const boards = computed(() => store.getters.mainBoards)
  const user = computed(() => store.getters.user)

  const defaultBoardLocalStorageKey = computed(() => `an-${user.value?.uid}`)
  const defaultBoard = ref(null)

  // Get the board ID from the route
  const routeBoardId = useRouteParams('boardId')

  watchEffect(() => {
    defaultBoard.value = localStorage.getItem(defaultBoardLocalStorageKey.value)
  })

  // Check if a board ID exists in the user's boards - delegate to service
  const isBoardInOrg = (boardId) => {
    return boardNavigationService.isValidBoardId(boardId)
  }

  // Get the validated board ID from the route or default
  const boardId = computed(() => {
    // First try from route params - this is the primary source
    if (routeBoardId.value && isBoardInOrg(routeBoardId.value)) {
      return routeBoardId.value
    }

    // Get default from service as fallback
    return boardNavigationService.getDefaultBoardId() || ''
  })

  const board = computed(() => boards.value.find((b) => b.boardId === boardId.value))

  // Change the user's default board - delegate to service
  const changeCurrentBoard = (newBoardId) => {
    if (!newBoardId || !isBoardInOrg(newBoardId)) return

    // Use the service for consistent persistence
    boardNavigationService.persistDefaultBoard(newBoardId)
    defaultBoard.value = newBoardId
    
    // Reset settings is still needed here since it's UI-specific
    store.dispatch('resetSettings')
  }

  const getLatestUploadDate = (boardId) => {
    const board = boards.value.find((b) => b.boardId === boardId)
    return board?.lastUploadDate ? new Date(board.lastUploadDate).toLocaleString() : null
  }

  return {
    boards,
    boardId,
    board,
    defaultBoard: readonly(defaultBoard),
    changeCurrentBoard,
    getLatestUploadDate,
    isBoardInOrg
  }
}
